function randomFromInterval(min, max) {
  return Math.random() * (max - min) + min;
}

function cubicBezier(P0, P1, P2, P3, t) {
  var x = (1 - t) ** 3 * P0[0] + 3 * (1 - t) ** 2 * t * P1[0] + 3 * (1 - t) * t ** 2 * P2[0] + t ** 3 * P3[0];
  var y = (1 - t) ** 3 * P0[1] + 3 * (1 - t) ** 2 * t * P1[1] + 3 * (1 - t) * t ** 2 * P2[1] + t ** 3 * P3[1];
  return [x, y];
}

// Generate cartoon-style dog head (continuous parameters only, no enums)
export function generateDogHeadShape() {
  // All parameters are continuous random values
  var headWidth = randomFromInterval(55, 100);
  var headHeight = randomFromInterval(60, 105);
  var headCenterY = randomFromInterval(-20, 5);

  // Snout parameters (continuous - creates natural variety)
  var snoutWidth = randomFromInterval(28, 60);
  var snoutLength = randomFromInterval(20, 75);  // Short to long snout
  var snoutStartY = headCenterY + headHeight * randomFromInterval(0.25, 0.45);

  // Asymmetry factor
  var asymmetryFactor = randomFromInterval(0.85, 1.15);

  // Head roundness (continuous parameter)
  var headRoundness = randomFromInterval(0.8, 1.05);

  var results = [];

  // Left ear connection point (we'll return this for ear attachment)
  var leftEarX = -headWidth * randomFromInterval(0.7, 0.9);
  var leftEarY = headCenterY - headHeight * randomFromInterval(0.3, 0.5);

  // Start from left ear attachment point
  results.push([leftEarX, leftEarY]);

  // Left side of head going up - with asymmetry
  var leftTopX = -headWidth * randomFromInterval(0.55, 0.75) * asymmetryFactor;
  var leftTopY = headCenterY - headHeight * randomFromInterval(0.75, 1.0);

  for (let t = 0; t <= 1; t += 0.05) {
    let point = cubicBezier(
      [leftEarX, leftEarY],
      [leftEarX * 0.8, leftTopY * 1.1],
      [leftTopX * 1.2, leftTopY * 1.05],
      [leftTopX, leftTopY],
      t
    );
    results.push(point);
  }

  // Top of head (rounded, adjusted by breed)
  var topY = headCenterY - headHeight;
  for (let t = 0; t <= 1; t += 0.05) {
    let point = cubicBezier(
      [leftTopX, leftTopY],
      [-headWidth * 0.3, topY * (0.95 + headRoundness * 0.1)],
      [headWidth * 0.3, topY * (0.95 + headRoundness * 0.1)],
      [headWidth * randomFromInterval(0.55, 0.75) / asymmetryFactor, leftTopY],
      t
    );
    results.push(point);
  }

  // Right ear connection point - asymmetric
  var rightEarX = headWidth * randomFromInterval(0.7, 0.9) / asymmetryFactor;
  var rightEarY = headCenterY - headHeight * randomFromInterval(0.3, 0.5);

  // Right side going down to ear point
  for (let t = 0; t <= 1; t += 0.05) {
    let point = cubicBezier(
      [headWidth * randomFromInterval(0.55, 0.75) / asymmetryFactor, leftTopY],
      [rightEarX * 0.8, leftTopY * 1.1],
      [rightEarX * 1.1, rightEarY * 0.9],
      [rightEarX, rightEarY],
      t
    );
    results.push(point);
  }

  // Right side of head going down to snout
  var rightSnoutX = snoutWidth / 2;
  for (let t = 0; t <= 1; t += 0.05) {
    let point = cubicBezier(
      [rightEarX, rightEarY],
      [headWidth * 0.7, snoutStartY * 0.5],
      [snoutWidth * 0.8, snoutStartY * 0.8],
      [rightSnoutX, snoutStartY],
      t
    );
    results.push(point);
  }

  // Right side of snout going down
  var snoutBottomY = snoutStartY + snoutLength;
  for (let t = 0; t <= 1; t += 0.05) {
    let point = cubicBezier(
      [rightSnoutX, snoutStartY],
      [snoutWidth * 0.6, snoutStartY + snoutLength * 0.3],
      [snoutWidth * 0.55, snoutStartY + snoutLength * 0.7],
      [snoutWidth * 0.4, snoutBottomY],
      t
    );
    results.push(point);
  }

  // Bottom of snout
  for (let t = 0; t <= 1; t += 0.05) {
    let point = cubicBezier(
      [snoutWidth * 0.4, snoutBottomY],
      [snoutWidth * 0.2, snoutBottomY + 5],
      [-snoutWidth * 0.2, snoutBottomY + 5],
      [-snoutWidth * 0.4, snoutBottomY],
      t
    );
    results.push(point);
  }

  // Left side of snout going up
  var leftSnoutX = -snoutWidth / 2;
  for (let t = 0; t <= 1; t += 0.05) {
    let point = cubicBezier(
      [-snoutWidth * 0.4, snoutBottomY],
      [-snoutWidth * 0.55, snoutStartY + snoutLength * 0.7],
      [-snoutWidth * 0.6, snoutStartY + snoutLength * 0.3],
      [leftSnoutX, snoutStartY],
      t
    );
    results.push(point);
  }

  // Left side from snout to head
  for (let t = 0; t <= 1; t += 0.05) {
    let point = cubicBezier(
      [leftSnoutX, snoutStartY],
      [-snoutWidth * 0.8, snoutStartY * 0.8],
      [-headWidth * 0.7, snoutStartY * 0.5],
      [leftEarX, leftEarY],
      t
    );
    results.push(point);
  }

  // Calculate bounds
  let minX = Math.min(...results.map(p => p[0]));
  let maxX = Math.max(...results.map(p => p[0]));
  let minY = Math.min(...results.map(p => p[1]));
  let maxY = Math.max(...results.map(p => p[1]));

  let width = maxX - minX;
  let height = maxY - minY;

  return {
    head: results,
    width: width,
    height: height,
    center: [0, 0],
    snoutBottomY: snoutBottomY,
    maxY: maxY,  // Actual bottom of head for neck connection
    leftEarPoint: [leftEarX, leftEarY],
    rightEarPoint: [rightEarX, rightEarY],
    headCenterY: headCenterY,
    headHeight: headHeight,
    snoutLength: snoutLength  // For nose sizing
  };
}
